<app-popup
  [isOpen]="isOpen()"
  [size]="rows().length > 0 ? 'xxlarge' : 'xlarge'"
  color="mint"
  variant="secondary"
  [title]="'RECORD_MODAL.TITLE' | translate"
  (closed)="closeDialog()">

  <div class="record-modal">

    <!-- ── Fil d'étapes ───────────────────────────────────────────────────── -->
    <ol class="steps">
      @for (item of steps(); track item.index) {
        <li
          class="steps__item"
          [class.steps__item--active]="item.index === step()"
          [class.steps__item--done]="item.index < step()"
          [class.steps__item--todo]="item.index > step()"
          (click)="goToStep(item.index)">
          <span class="steps__number">{{ item.index }}</span>
          <span class="steps__name">{{ item.translate ? (item.label | translate) : item.label }}</span>
        </li>
      }
    </ol>

    <hr class="record-modal__separator" />

    <form class="record-modal__content" [formGroup]="manualForm" (ngSubmit)="submitManualRecord()">

      <!-- ── Étape 1 : point de mesure ─────────────────────────────────────── -->
      @if (step() === 1) {
        <div class="field">
          <div class="field__label">
            {{ 'RECORD_MODAL.NAME' | translate }}<span class="field__required">*</span>
          </div>
          <app-select
            [options]="controlPointOptions()"
            size="large"
            formControlName="controlPointId"
            [placeholder]="'RECORD_MODAL.SELECT_MEASUREMENT_POINT' | translate">
          </app-select>
        </div>
      }

      <!-- ── Étape 2 : type de saisie ──────────────────────────────────────── -->
      @if (step() === 2) {
        <div class="input-types">
          @for (type of inputTypes; track type.key) {
            <button
              type="button"
              class="input-type"
              [class.input-type--selected]="inputType() === type.key"
              (click)="selectInputType(type.key)">
              <img class="input-type__icon input-type__icon--{{ type.key }}" [src]="type.icon" alt="" />
              <span class="input-type__label">{{ type.label | translate }}</span>
            </button>
          }
        </div>
      }

      <!-- ── Étape 3 : saisie manuelle ─────────────────────────────────────── -->
      @if (step() === 3 && inputType() === 'manual') {
        <div class="field">
          <div class="field__label">{{ 'VIBRATION.DISTANCE' | translate }}</div>
          <app-input type="number" formControlName="distance" placeholder="e.g. 100"></app-input>
          @if (isFieldInvalid('distance')) {
            <div class="field__error">{{ 'FORM.REQUIRED_POSITIVE_NUMBER' | translate }}</div>
          }
        </div>

        <div class="field">
          <div class="field__label">{{ 'VIBRATION.OVERPRESSURE' | translate }}</div>
          <app-input type="number" formControlName="overpressure" placeholder="e.g. 1.5"></app-input>
          @if (isFieldInvalid('overpressure')) {
            <div class="field__error">{{ 'FORM.REQUIRED_POSITIVE_NUMBER' | translate }}</div>
          }
        </div>

        <div class="field">
          <div class="field__label">{{ 'VIBRATION.PIC' | translate }}</div>
          <app-input type="number" formControlName="pic" placeholder="e.g. 2.3"></app-input>
          @if (isFieldInvalid('pic')) {
            <div class="field__error">{{ 'FORM.REQUIRED_POSITIVE_NUMBER' | translate }}</div>
          }
        </div>
      }

    </form>

    <!-- ── Étape 3 : import de fichiers ──────────────────────────────────────── -->
    @if (step() === 3 && inputType() === 'upload') {

      @if (rows().length > 0) {
        <app-scrollable class="record-modal__list">
          <app-data-table class="record-table"
            [data]="rows()"
            [columns]="tableColumns"
            rowKey="id"
            sortAscIcon="assets/icon/new_icon/arrow_full_down.svg"
            sortDescIcon="assets/icon/new_icon/arrow_full_up.svg"
            sortIconFilter="brightness(0)">

            <ng-template tableCell="fileName" let-row>
              <div class="record-line">
                <img src="assets/icon/report.svg" alt="" />
                <div class="record-table__stack">
                  <span class="record-table__filename">{{ row.fileName || '-' }}</span>
                  @if (row.sizeLabel) {
                    <span class="record-table__muted">{{ row.sizeLabel }}</span>
                  }
                </div>
              </div>
            </ng-template>

            <ng-template tableCell="timestampMs" let-row>
              <div class="record-table__stack">
                <div>{{ row.dateLabel }}</div>
                <div>{{ row.timeLabel }}</div>
              </div>
            </ng-template>

            <ng-template tableCell="notes" let-row>
              <span>{{ row.notes || '-' }}</span>
            </ng-template>

            <ng-template tableCell="maxPpv" let-row>
              @if (row.channelMaxes.length > 0) {
                <div class="channel-maxes">
                  @for (channel of row.channelMaxes; track channel.label) {
                    <div class="channel-maxes__item">
                      <span class="record-table__muted">{{ channel.label }}</span>
                      <span>{{ channel.value | number: '1.2-2' }}</span>
                    </div>
                  }
                </div>
              } @else {
                <span class="record-table__muted">-</span>
              }
            </ng-template>

            <ng-template tableCell="deviceLabel" let-row>
              <div class="record-line">
                <img src="assets/icon/device.svg" alt="" />
                <span>{{ row.deviceLabel || '-' }}</span>
              </div>
            </ng-template>

            <!-- Retrait de l'enregistrement de la sélection -->
            <ng-template tableCell="remove" let-row>
              <app-button variant="tertiary" size="xsmall" [addLabel]="false" [addIconLeft]="true" (clicked)="removeRecord(row)">
                <img slot="icon-left" src="assets/icon/close.svg" alt="" />
              </app-button>
            </ng-template>

            <!-- Deuxième ligne de la carte : un mini-graphe par canal -->
            <ng-template tableCell="sparklines" let-row>
              @if (row.sparklines.length > 0) {
                <div class="record-table__charts">
                  @for (spark of row.sparklines; track spark.label) {
                    <div class="mini-chart">
                      <span class="mini-chart__name">{{ spark.label }}</span>
                      <div class="mini-chart__yaxis">
                        <span>{{ spark.max | number: '1.1-1' }}</span>
                        <span>{{ spark.min | number: '1.1-1' }}</span>
                      </div>
                      <div class="mini-chart__body">
                        <svg viewBox="0 0 120 40" preserveAspectRatio="none" class="mini-chart__svg">
                          <polyline [attr.points]="spark.points" fill="none" stroke="var(--c-amarante, #6c1d45)" stroke-width="1.2"></polyline>
                        </svg>
                        <div class="mini-chart__ticks">
                          @for (tick of spark.ticks; track $index) {
                            <span>{{ tick }}</span>
                          }
                        </div>
                      </div>
                    </div>
                  }
                </div>
              }
            </ng-template>

          </app-data-table>
        </app-scrollable>
      }

      <!-- Progression de l'envoi -->
      @if (uploadProgress() !== null) {
        <div class="record-modal__progress">
          <div class="record-modal__progress-bar" [style.width.%]="uploadProgress()">{{ uploadProgress() }}%</div>
        </div>
      }

      <!-- Zone de dépôt : occupe tout l'espace tant qu'aucun fichier n'est importé -->
      <div
        class="dropzone"
        [class.dropzone--compact]="rows().length > 0"
        (click)="openFilePicker()"
        (drop)="onFileDrop($event)"
        (dragover)="onDragOver($event)">
        <input type="file" class="dropzone__input" multiple accept=".sis,.csv" (change)="onFileInputChange($event)" #fileInput />
        <p class="dropzone__title">{{ 'RECORD_MODAL.DROP_OR_CLICK' | translate }}</p>
        <p class="dropzone__hint">{{ 'RECORD_MODAL.ACCEPTED_FILES' | translate }}</p>
      </div>
    }

    @if (errorMessage() !== '') {
      <div class="record-modal__error">
        <img src="assets/icon/Polygon_danger.svg" alt="" />
        <span>{{ errorMessage() }}</span>
        <app-button variant="tertiary" size="xsmall" [addLabel]="false" [addIconLeft]="true" (clicked)="closeErrorMessage()">
          <img slot="icon-left" src="assets/icon/close.svg" alt="" />
        </app-button>
      </div>
    }

  </div>

  <!-- ── Actions ──────────────────────────────────────────────────────────── -->
  <div slot="footer" class="record-modal__actions">
    <app-button
      class="record-modal__cancel"
      variant="secondary"
      size="medium"
      color="mint"
      [label]="'BUTTONS.CANCEL' | translate"
      (clicked)="closeDialog()">
    </app-button>

    @if (step() < 3) {
      <app-button
        variant="primary"
        size="medium"
        color="mint"
        [addIconRight]="true"
        [state]="canContinue() ? 'default' : 'disabled'"
        [label]="'RECORD_MODAL.CONTINUE' | translate"
        (clicked)="continue()">
        <img slot="icon-right" src="assets/icon/new_icon/directional-arrow-right.svg" alt="" />
      </app-button>
    } @else if (inputType() === 'manual') {
      <app-button
        variant="primary"
        size="medium"
        color="mint"
        [addIconRight]="true"
        [state]="isManualSubmitting() ? 'disabled' : 'default'"
        [label]="'BUTTONS.CONFIRM' | translate"
        (clicked)="submitManualRecord()">
        <img slot="icon-right" src="assets/icon/new_icon/check.svg" alt="" />
      </app-button>
    } @else {
      <app-button
        variant="primary"
        size="medium"
        color="mint"
        [addIconRight]="true"
        [state]="isUploading() || isSaving() ? 'disabled' : 'default'"
        [label]="'BUTTONS.CONFIRM' | translate"
        (clicked)="confirmUpload()">
        <img slot="icon-right" src="assets/icon/new_icon/check.svg" alt="" />
      </app-button>
    }
  </div>

</app-popup>
